import React, { useMemo, useState } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Theme, alpha } from '@mui/material/styles';

import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { AVAILABILITY, LIST_VISIBILITY, ONLINE_PURCHASE } from '~/consts/schedule';

import { formatDateSlashesWithClock, isBetween } from '~/services/TimeService';

import CampaignScheduleBrandGroupModal from './CampaignScheduleBrandGroupModal';
import { CampaignScheduleDropdownRow } from './CampaignScheduleExpandableRow';
import { CUSTOMER_FACING_TYPES, buildCampaignScheduleBrandRows } from './campaignScheduleBrandRows';
import { getScheduleTypeLabel } from './campaignScheduleHistoryDisplay';
import { BRAND_ROWS_TABLE_COLUMN_COUNT, handleExpandRowKeyDown, toggleExpandedRow } from './campaignScheduleTabLayout';
import type { BrandSchedule, CampaignScheduleDateEntry } from './types';

// lux_plus is shown bare (its row's Type chip already identifies it); every other type present gets
// a labeled, tooltipped pill above its date range.
const PILL_CONFIG: Partial<
  Record<BrandSchedule['type'], { color: 'warning' | 'default' | 'primary'; tooltip: string }>
> = {
  [AVAILABILITY]: { color: 'default', tooltip: 'Staff-only purchasing; BNBL date cutoff' },
  [ONLINE_PURCHASE]: { color: 'warning', tooltip: 'Purchasable via direct link (not in search)' },
  [LIST_VISIBILITY]: { color: 'primary', tooltip: 'Visible in search, carousels, and homepage' },
};

type RowStatus = 'active' | 'availableOffline' | 'availableForStaff' | 'inactive';

const ROW_STATUS_CONFIG: Record<RowStatus, { label: string; color: 'success' | 'warning' | 'info' | undefined }> = {
  active: { label: 'Active', color: 'success' },
  availableOffline: { label: 'Available Offline', color: 'warning' },
  availableForStaff: { label: 'Available for Staff', color: 'info' },
  inactive: { label: 'Inactive', color: undefined },
};

function getRowStatusColor(status: RowStatus, theme: Theme) {
  switch (status) {
    case 'active':
      return theme.palette.success.main;
    case 'availableOffline':
      return theme.palette.warning.main;
    case 'availableForStaff':
      return theme.palette.info.main;
    case 'inactive':
      return theme.palette.grey[500];
  }
}

// Reflects the most customer-visible window currently active, in descending order of visibility:
// list_visibility/lux_plus (findable in search) > online_purchase (purchasable via direct link
// only) > availability (staff-only purchasing). A lingering availability/online_purchase window
// with no matching list_visibility never counts as "Active" — that's reserved for real search
// visibility — but it's still surfaced instead of being lumped in with a fully dead schedule.
function getRowStatus(dateEntries: Array<CampaignScheduleDateEntry>): RowStatus {
  const now = new Date();
  const activeTypes = new Set(
    dateEntries.filter((entry) => isBetween(entry.start, entry.end, now, undefined, '[]')).map((entry) => entry.type),
  );

  if (CUSTOMER_FACING_TYPES.some((type) => activeTypes.has(type))) {
    return 'active';
  }
  if (activeTypes.has(ONLINE_PURCHASE)) {
    return 'availableOffline';
  }
  if (activeTypes.has(AVAILABILITY)) {
    return 'availableForStaff';
  }
  return 'inactive';
}

interface Props {
  emptyMessage: string;
  offerId: string | undefined;
  schedules: Array<BrandSchedule>;
}

export default function CampaignScheduleBrandRowsTable({ emptyMessage, offerId, schedules }: Props) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  // Keyed lookup (not the row object itself) so the modal picks up fresh data after a save/delete
  // invalidates the offer query and brandRows is recomputed, instead of showing a stale snapshot.
  const [manageRowKey, setManageRowKey] = useState<string | undefined>();
  const brandRows = useMemo(() => buildCampaignScheduleBrandRows(schedules), [schedules]);
  const manageRow = brandRows.find((row) => row.key === manageRowKey);

  if (brandRows.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ py: 1.5, textAlign: 'center' }} variant="body2">
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Schedule</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Dates</TableCell>
            <TableCell>Regions</TableCell>
            <TableCell sx={{ width: 96 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {brandRows.map((row) => {
            const isExpanded = expandedRows.has(row.key);
            const status = getRowStatus(row.dateEntries);
            const statusConfig = ROW_STATUS_CONFIG[status];

            return (
              <React.Fragment key={row.key}>
                <TableRow>
                  <TableCell>
                    <Typography fontWeight={700} variant="body2">
                      {row.brand}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      color={row.isLuxPlus ? undefined : 'info'}
                      label={row.isLuxPlus ? 'LuxPlus' : 'Normal'}
                      size="small"
                      sx={(theme) => ({
                        bgcolor: alpha(row.isLuxPlus ? theme.palette.luxPlus.main : theme.palette.info.main, 0.12),
                        color: row.isLuxPlus ? theme.palette.luxPlus.main : undefined,
                        borderColor: row.isLuxPlus ? theme.palette.luxPlus.main : undefined,
                        fontWeight: 600,
                      })}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      color={statusConfig.color}
                      label={statusConfig.label}
                      size="small"
                      sx={{
                        bgcolor: (theme) => alpha(getRowStatusColor(status, theme), 0.12),
                        fontWeight: 600,
                      }}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Stack spacing={1}>
                      {row.dateEntries.map((entry) => {
                        const pillConfig = PILL_CONFIG[entry.type];

                        return (
                          <Stack key={entry.type} spacing={0.5}>
                            {pillConfig && (
                              <Tooltip arrow title={pillConfig.tooltip}>
                                <Chip
                                  color={pillConfig.color}
                                  label={getScheduleTypeLabel(entry.type)}
                                  size="small"
                                  sx={{ alignSelf: 'flex-start' }}
                                  variant="outlined"
                                />
                              </Tooltip>
                            )}
                            <Typography variant="body2">
                              {formatDateSlashesWithClock(entry.start)} → {formatDateSlashesWithClock(entry.end)}
                            </Typography>
                          </Stack>
                        );
                      })}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack
                      alignItems="center"
                      direction="row"
                      role="button"
                      spacing={0.5}
                      sx={{ cursor: 'pointer' }}
                      tabIndex={0}
                      onClick={() => setExpandedRows((current) => toggleExpandedRow(current, row.key))}
                      onKeyDown={(event) =>
                        handleExpandRowKeyDown(event, () =>
                          setExpandedRows((current) => toggleExpandedRow(current, row.key)),
                        )
                      }
                    >
                      <Typography variant="body2">
                        {row.regions.length === 0
                          ? 'No regions'
                          : row.regions.length === 1
                            ? row.regions[0]
                            : `${row.regions.length} regions`}
                      </Typography>
                      {isExpanded ? (
                        <KeyboardArrowDownIcon fontSize="small" />
                      ) : (
                        <KeyboardArrowRightIcon fontSize="small" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <PermissionedComponent>
                      <Tooltip title={`Edit ${row.brand} schedules`}>
                        {/* Tooltip needs a non-disabled child to attach its listeners to — wrapping in a
                            span keeps the tooltip working (and explains why) even while the button itself
                            is disabled. See https://mui.com/material-ui/react-tooltip/#disabled-elements */}
                        <span>
                          <IconButton
                            aria-label={`Edit ${row.brand} schedules`}
                            disabled={!offerId}
                            size="small"
                            onClick={() => setManageRowKey(row.key)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </PermissionedComponent>
                  </TableCell>
                </TableRow>
                <CampaignScheduleDropdownRow colSpan={BRAND_ROWS_TABLE_COLUMN_COUNT} isExpanded={isExpanded}>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {row.regions.map((region) => (
                      <Chip key={region} label={region} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </CampaignScheduleDropdownRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
      {offerId && (
        <CampaignScheduleBrandGroupModal
          offerId={offerId}
          open={!!manageRowKey}
          row={manageRow}
          onClose={() => setManageRowKey(undefined)}
        />
      )}
    </>
  );
}
